/**
 * How long a coach has to accept an incoming call before the candidate side
 * sends call_missed. Shared with IncomingCallCard so the visual countdown
 * stays in sync with the actual timeout (CTS-9776).
 */
export const CALL_RING_TIMEOUT_MS = 30000

/**
 * CallService handles all call-related functionality including initiation, acceptance, and management
 *
 * This service manages the lifecycle of calls between candidates and coaches, supporting
 * three call types: text, audio, and video. It coordinates with RtcService for WebRTC
 * connections and provides event notifications for UI components.
 *
 * @class CallService
 */
class CallService {
  /**
   * Creates a new CallService instance
   *
   * Initializes all internal state for managing calls, media streams, and event listeners.
   * The service maintains a single active call at a time and tracks both local and remote
   * media streams for video/audio calls.
   */
  constructor() {
    /** @type {Map<string, Set<Function>>} Map of event names to their callback sets */
    this.listeners = new Map()

    /** @type {Object|null} The currently active call object, or null if no active call */
    this.activeCall = null

    /** @type {MediaStream|null} Local media stream (this user's video/audio) */
    this.localStream = null

    /** @type {MediaStream|null} Remote media stream (the other participant's video/audio) */
    this.remoteStream = null

    /** @type {HTMLVideoElement|null} DOM element for displaying local video */
    this.localVideoElement = null

    /** @type {HTMLVideoElement|null} DOM element for displaying remote video */
    this.remoteVideoElement = null

    /** @type {Function|null} Callback function for video state changes in React components */
    this.onVideoStateChange = null

    /** @type {number|null} Timeout ID for call timeout when coach doesn't answer */
    this.callTimeout = null
  }

  /**
   * Initiates a call to a coach
   *
   * Creates a new call request and sends it to the specified coach. The call will be
   * in a "ringing" state until the coach accepts, rejects, or misses it. Prevents
   * duplicate calls to the same coach.
   *
   * @param {string} coachEasyrtcid - The EasyRTC ID of the coach to call
   * @param {string} [callType='text'] - Type of call: 'text', 'audio', or 'video'
   * @param {RtcService} rtcService - The RTC service instance for EasyRTC operations
   * @param {string|null} [conversationId=null] - Optional conversation ID to use, otherwise generates a new one
   * @returns {Object} The active call object with status 'initiating' or 'ringing'
   * @throws {Error} If not connected to EasyRTC or EasyRTC instance is unavailable
   */
  initiateCall(
    coachEasyrtcid,
    callType = 'text',
    rtcService,
    conversationId = null,
  ) {
    const easyrtc = rtcService.getEasyRTC()
    const connectionState = rtcService.getConnectionState()

    if (!easyrtc || !connectionState.isConnected) {
      throw new Error('Not connected to EasyRTC')
    }

    // CTS-9831: Re-check live room presence before call_start (UI may be stale)
    const chatRoom = connectionState.chatRoom
    if (chatRoom && easyrtc.getRoomOccupantsAsMap) {
      const occupants = easyrtc.getRoomOccupantsAsMap(chatRoom) || {}
      const coachOccupant = occupants[coachEasyrtcid]
      const presenceShow = coachOccupant?.presence?.show
      if (!coachOccupant || presenceShow === 'away') {
        const error = new Error('Coach is unavailable')
        error.code = 'COACH_UNAVAILABLE'
        throw error
      }
    }

    // Prevent initiating multiple calls
    if (this.activeCall && this.activeCall.status === 'initiating') {
      return this.activeCall
    }

    // Prevent initiating calls to the same coach if one is already active
    if (
      this.activeCall &&
      this.activeCall.coachEasyrtcid === coachEasyrtcid &&
      ['initiating', 'connected'].includes(this.activeCall.status)
    ) {
      return this.activeCall
    }

    // Use passed conversationId if provided, otherwise generate a new one
    const finalConversationId = conversationId || this.generateConversationId()
    this.activeCall = {
      coachEasyrtcid,
      callType,
      conversationId: finalConversationId,
      coachName: rtcService.getCoachName(coachEasyrtcid) || '',
      status: 'initiating',
      startTime: Date.now(),
    }

    // Update the connection state to reflect the chat mode
    if (rtcService.updateConnectionState) {
      rtcService.updateConnectionState({
        chatMode: callType,
        activeCall: this.activeCall,
      })
    }

    // Send call_start message first (required by old system)
    try {
      easyrtc.sendData(
        coachEasyrtcid,
        'call_start',
        connectionState.myEasyrtcid,
        undefined,
        finalConversationId,
      )

      // Update call status to "ringing" - waiting for coach response
      this.activeCall.status = 'ringing'
      this.notifyListeners('callRinging', this.activeCall)

      // Set timeout to send call_missed if coach doesn't answer within CALL_RING_TIMEOUT_MS
      this.clearCallTimeout() // Clear any existing timeout
      this.callTimeout = setTimeout(() => {
        // Only send call_missed if call is still ringing
        if (
          this.activeCall &&
          this.activeCall.status === 'ringing' &&
          this.activeCall.coachEasyrtcid === coachEasyrtcid
        ) {
          try {
            // Get fresh references to ensure they're still valid
            const currentEasyrtc = rtcService.getEasyRTC()
            const currentConnectionState = rtcService.getConnectionState()
            if (currentEasyrtc && currentConnectionState.isConnected) {
              currentEasyrtc.sendData(
                coachEasyrtcid,
                'call_missed',
                currentConnectionState.myEasyrtcid,
                undefined,
                finalConversationId,
              )
              // Handle the missed call locally
              this.handleCallMissedByCoach(coachEasyrtcid, null)
            }
          } catch (error) {
            console.error(
              'CallService - Error sending call_missed message:',
              error,
            )
          }
        }
        this.callTimeout = null
      }, CALL_RING_TIMEOUT_MS)

      // If this is a video call, trigger the video loading state
      if (callType === 'video') {
        this.notifyListeners('videoLoadingStarted', {
          callType,
          conversationId: finalConversationId,
        })
      }
    } catch (error) {
      console.error('CallService - Error sending call_start message:', error)
    }

    this.notifyListeners('callInitiated', this.activeCall)

    return this.activeCall
  }

  /**
   * Performs the actual EasyRTC peer connection after coach accepts the call
   *
   * This method is called after the coach accepts a video or audio call. It initializes
   * media streams (if needed) and establishes the WebRTC peer connection. For text calls,
   * this is not used as they don't require media streams.
   *
   * @param {string} targetEasyrtcid - The EasyRTC ID of the coach to connect to
   * @param {string} callType - Type of call: 'audio' or 'video' (text calls don't use this)
   * @param {string} conversationId - The conversation ID for this call
   * @param {RtcService} rtcService - The RTC service instance for EasyRTC operations
   * @returns {Promise<void>}
   */
  async performCall(targetEasyrtcid, callType, conversationId, rtcService) {
    const easyrtc = rtcService.getEasyRTC()
    if (!easyrtc) {
      console.error('CallService - EasyRTC not available for performCall')
      return
    }

    // Initialize media right before the call to ensure same easyrtc instance
    if (callType === 'video' || callType === 'audio') {
      try {
        // Wait for media to be ready before proceeding
        await this.initializeMediaForCall(callType, conversationId, easyrtc)
      } catch (error) {
        console.error('CallService - Media initialization failed:', error)
        // Continue with call even if media fails - user can still have text chat
      }
    }

    const successCallback = (_easyrtcid) => {
      // console.log('CallService - EasyRTC call success callback fired')
      if (this.activeCall) {
        // console.log('CallService - Setting activeCall status to connected')
        this.activeCall.status = 'connected'

        // Update presence to indicate we're in a chat based on call type
        const coachName = this.activeCall.coachName || 'coach'
        if (callType === 'video') {
          rtcService.updateCandidatePresenceInVideoChat(
            coachName,
            conversationId,
          )
        } else if (callType === 'audio') {
          rtcService.updateCandidatePresenceInAudioChat(
            coachName,
            conversationId,
          )
        }

        // console.log('CallService - Firing callConnected event')
        this.notifyListeners('callConnected', this.activeCall)
      } else {
        console.error('CallService - No activeCall in success callback')
      }
    }

    const failureCallback = (errorCode, errorText) => {
      if (this.activeCall) {
        this.activeCall.status = 'failed'
        this.notifyListeners('callFailed', { errorCode, errorText })
      }
    }

    try {
      /*
      console.log('CallService - Calling easyrtc.call with:', {
        targetEasyrtcid,
        conversationId,
      })
      */
      easyrtc.call(
        targetEasyrtcid,
        successCallback,
        failureCallback,
        undefined, // No accepted callback needed - we handle acceptance via messages
        undefined,
        conversationId,
      )
      // console.log('CallService - easyrtc.call initiated successfully')
    } catch (error) {
      console.error('CallService - Error calling easyrtc.call:', error)
      failureCallback('CALL_ERROR', error.message)
    }
  }

  /**
   * Initializes media streams for video or audio calls
   *
   * Sets up local media capture (camera/microphone) for the call. For video calls,
   * enables both video and audio. For audio calls, only enables audio. The media
   * stream is stored and made available to React components via event notifications.
   *
   * @param {string} callType - Type of call: 'video' or 'audio'
   * @param {string} conversationId - The conversation ID for this call
   * @param {Object} easyrtc - The EasyRTC instance to use for media initialization
   * @returns {Promise<void>}
   */
  async initializeMediaForCall(callType, conversationId, easyrtc) {
    try {
      if (callType === 'video') {
        // Set up video for the call
        easyrtc.setAutoInitUserMedia(true)
        easyrtc.enableVideo(true)
        easyrtc.enableAudio(true)

        // Initialize media source
        easyrtc.initMediaSource(
          (mediastream) => {
            // Store the local stream for React components to access
            this.localStream = mediastream

            // Set up self video element
            const selfVideo = document.getElementById('selfVideo')
            if (selfVideo && mediastream) {
              easyrtc.setVideoObjectSrc(selfVideo, mediastream, conversationId)
              selfVideo.muted = true
              selfVideo.style.display = 'block'
            }

            // Notify ChatManager that local video is ready
            // console.log('CallService - About to fire localVideoReady event')
            this.notifyListeners('localVideoReady', {
              stream: mediastream,
              hasLocalVideo: true,
              hasLocalAudio: true,
            })
          },
          (errorCode, errorDesc) => {
            console.error(
              'CallService - Video media initialization failed:',
              errorCode,
              errorDesc,
            )
            // Continue with call even if video fails
          },
          undefined,
          conversationId,
        )
      } else if (callType === 'audio') {
        // Set up audio for the call
        easyrtc.setAutoInitUserMedia(true)
        easyrtc.enableVideo(false)
        easyrtc.enableAudio(true)

        // Initialize media source
        easyrtc.initMediaSource(
          (mediastream) => {
            // console.log('CallService - Audio media initialized successfully')

            // Store the local stream for React components to access
            this.localStream = mediastream

            // Set up self video element (for avatar display)
            const selfVideo = document.getElementById('selfVideo')
            if (selfVideo && mediastream) {
              easyrtc.setVideoObjectSrc(selfVideo, mediastream, conversationId)
              selfVideo.muted = true
              selfVideo.style.display = 'block'
            }

            // Notify ChatManager that local audio is ready
            this.notifyListeners('localAudioReady', {
              stream: mediastream,
              hasLocalVideo: false,
              hasLocalAudio: true,
            })
          },
          (errorCode, errorDesc) => {
            console.error(
              'CallService - Audio media initialization failed:',
              errorCode,
              errorDesc,
            )
            // Continue with call even if audio fails
          },
          undefined,
          conversationId,
        )
      }
    } catch (error) {
      console.error('CallService - Error initializing media:', error)
      // Continue with call even if media initialization fails
    }
  }

  /**
   * Handles when the coach accepts the call via call_accepted message
   *
   * This is called when a call_accepted message is received from the coach. For text
   * calls, immediately marks the call as connected. For video/audio calls, initiates
   * the actual WebRTC peer connection via performCall().
   *
   * @param {string} coachEasyrtcid - The EasyRTC ID of the coach who accepted
   * @param {Object} _messageData - Message data (currently unused)
   * @param {RtcService} rtcService - The RTC service instance for EasyRTC operations
   */
  handleCallAcceptedByCoach = (coachEasyrtcid, _messageData, rtcService) => {
    /*
    console.log(
      'CallService - Call accepted by coach via message:',
      coachEasyrtcid,
    )
    */

    // Clear the call timeout since coach answered
    this.clearCallTimeout()

    if (this.activeCall && this.activeCall.coachEasyrtcid === coachEasyrtcid) {
      /*
      console.log(
        'CallService - Coach accepted call, callType:',
        this.activeCall.callType,
      )
      */

      // For text calls, immediately set the call as connected since the coach has accepted
      if (this.activeCall.callType === 'text') {
        /*
        console.log(
          'CallService - Text call accepted, immediately setting as connected',
        )
        */
        this.activeCall.status = 'connected'

        // Update presence to show candidate is in text chat
        if (rtcService) {
          const coachName = this.activeCall.coachName || 'coach'
          rtcService.updateCandidatePresenceInTextChat(
            coachName,
            this.activeCall.conversationId,
          )
        }

        this.notifyListeners('callConnected', this.activeCall)
      } else {
        // For video/audio calls, initiate the actual EasyRTC call
        /*
        console.log(
          'CallService - Video/audio call accepted, now initiating EasyRTC peer connection',
        )
        */
        this.performCall(
          coachEasyrtcid,
          this.activeCall.callType,
          this.activeCall.conversationId,
          rtcService,
        )
      }
    }
  }

  /**
   * Handles when the coach rejects the call via call_rejected message
   *
   * Updates the call status to 'rejected', notifies listeners, and clears the
   * active call. Only processes rejections if the call is currently in 'ringing' state.
   *
   * @param {string} _coachEasyrtcid - The EasyRTC ID of the coach who rejected (currently unused)
   * @param {Object} _messageData - Message data (currently unused)
   */
  handleCallRejectedByCoach = (_coachEasyrtcid, _messageData) => {
    /*
    console.log(
      'CallService - Call rejected by coach via message:',
      _coachEasyrtcid,
    )
    */

    // Clear the call timeout since coach responded
    this.clearCallTimeout()

    if (this.activeCall && this.activeCall.status === 'ringing') {
      // Update call status
      this.activeCall.status = 'rejected'

      // Notify listeners
      this.notifyListeners('callRejected', this.activeCall)

      // Clear the active call
      this.activeCall = null
    }
  }

  /**
   * Handles when the coach misses the call via call_missed message
   *
   * Updates the call status to 'missed', notifies listeners, and clears the
   * active call. Only processes missed calls if the call is currently in 'ringing' state.
   * This typically happens when the coach doesn't respond within a timeout period.
   *
   * @param {string} _coachEasyrtcid - The EasyRTC ID of the coach who missed (currently unused)
   * @param {Object} _messageData - Message data (currently unused)
   */
  handleCallMissedByCoach = (_coachEasyrtcid, _messageData) => {
    // console.log(
    //   'CallService - Call missed by coach via message:',
    //   _coachEasyrtcid,
    //   'activeCall:',
    //   this.activeCall,
    // )

    if (this.activeCall && this.activeCall.status === 'ringing') {
      // Update call status
      this.activeCall.status = 'missed'
      // console.log(
      //   'CallService - Updated activeCall status to missed:',
      //   this.activeCall,
      // )

      // Notify listeners
      this.notifyListeners('callMissed', this.activeCall)
      // console.log('CallService - Notified listeners of callMissed event')

      // Clear the active call
      this.activeCall = null
    } else {
      // console.log(
      //   'CallService - No active call or call not ringing, activeCall:',
      //   this.activeCall,
      // )
    }
  }

  /**
   * Handles video streams being added (both local and remote)
   *
   * Called by EasyRTC when media streams become available. Updates the appropriate
   * video element and notifies React components via the video state callback.
   *
   * @param {string} _easyrtcid - The EasyRTC ID (currently unused)
   * @param {MediaStream} stream - The media stream to attach
   * @param {string} type - Stream type: 'local' (this user's stream) or 'remote' (the other participant's stream)
   */
  handleVideoAdded(_easyrtcid, stream, type) {
    // console.log('Video added:', { easyrtcid: _easyrtcid, type, streamId: stream?.id })

    if (type === 'local') {
      // Handle local video (this user's own video)
      this.localStream = stream
      if (this.localVideoElement) {
        this.localVideoElement.srcObject = stream
        this.localVideoElement.style.display = 'block'
      }

      // Update React state
      if (this.onVideoStateChange) {
        this.onVideoStateChange({
          localStream: stream,
          localVideoElement: this.localVideoElement,
          hasLocalVideo: true,
        })
      }

      // console.log('Local video set up successfully')
    } else if (type === 'remote') {
      // Handle remote video (the other participant's video)
      this.remoteStream = stream
      if (this.remoteVideoElement) {
        this.remoteVideoElement.srcObject = stream
        this.remoteVideoElement.style.display = 'block'
      }

      // Update React state
      if (this.onVideoStateChange) {
        this.onVideoStateChange({
          remoteStream: stream,
          remoteVideoElement: this.remoteVideoElement,
          hasRemoteVideo: true,
        })
      }

      // console.log('Remote video set up successfully')
    }
  }

  /**
   * Sets video elements for React components to use
   *
   * Registers DOM video elements that will be used to display local and remote
   * video streams. These elements are updated automatically when streams become available.
   *
   * @param {HTMLVideoElement|null} localVideoElement - DOM element for local video display
   * @param {HTMLVideoElement|null} remoteVideoElement - DOM element for remote video display
   */
  setVideoElements(localVideoElement, remoteVideoElement) {
    this.localVideoElement = localVideoElement
    this.remoteVideoElement = remoteVideoElement
    /*
    console.log('Video elements set:', {
      localVideoElement,
      remoteVideoElement,
    })
    */
  }

  /**
   * Sets callback for React state updates
   *
   * Registers a callback function that will be invoked when video state changes
   * (e.g., when streams are added or removed). This allows React components to
   * update their state in response to media changes.
   *
   * @param {Function|null} callback - Callback function that receives video state updates
   */
  setVideoStateCallback(callback) {
    this.onVideoStateChange = callback
    // console.log('Video state callback set')
  }

  /**
   * Generates a unique conversation ID
   *
   * Creates a unique identifier for a conversation using timestamp and random string.
   * Format: "conv_{timestamp}_{randomString}"
   *
   * @returns {string} A unique conversation ID
   */
  generateConversationId() {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Adds an event listener for call-related events
   *
   * Registers a callback function to be called when a specific event occurs.
   * Supported events include: 'callInitiated', 'callRinging', 'callConnected',
   * 'callRejected', 'callMissed', 'callFailed', 'callEnded', 'localVideoReady',
   * 'localAudioReady', 'videoLoadingStarted'.
   *
   * @param {string} event - The event name to listen for
   * @param {Function} callback - The callback function to invoke when the event occurs
   */
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  /**
   * Removes an event listener
   *
   * Unregisters a previously registered callback function for a specific event.
   *
   * @param {string} event - The event name
   * @param {Function} callback - The callback function to remove
   */
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  /**
   * Notifies all registered listeners of an event
   *
   * Invokes all callback functions registered for the given event with the provided data.
   * Errors in individual callbacks are caught and logged without affecting other listeners.
   *
   * @param {string} event - The event name
   * @param {*} data - Data to pass to the event listeners
   */
  notifyListeners(event, data) {
    /*
    console.log(`CallService - Notifying listeners for event: ${event}`, {
      hasListeners: this.listeners.has(event),
      listenerCount: this.listeners.has(event)
        ? this.listeners.get(event).size
        : 0,
      data,
    })
    */

    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    }
  }

  /**
   * Clears the call timeout
   *
   * Clears any pending timeout for call missed detection. Should be called
   * when a call is accepted, rejected, or otherwise terminated.
   *
   * @private
   */
  clearCallTimeout() {
    if (this.callTimeout) {
      clearTimeout(this.callTimeout)
      this.callTimeout = null
    }
  }

  /**
   * Releases local media streams held by the call service
   */
  releaseLocalMedia() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        try {
          track.stop()
        } catch (error) {
          console.warn('CallService - Error stopping media track:', error)
        }
      })
      this.localStream = null
    }

    if (this.localVideoElement) {
      this.localVideoElement.srcObject = null
    }
  }

  /**
   * Cleans up the active call
   *
   * Ends the current call, clears the active call state, and notifies listeners
   * that the call has ended. This should be called when a call is terminated
   * or when the component unmounts.
   */
  cleanup() {
    // console.log('CallService - Cleaning up active call')
    // Clear any pending timeout
    this.clearCallTimeout()
    if (this.activeCall) {
      this.activeCall = null
      this.notifyListeners('callEnded', null)
    }
  }

  /**
   * Gets the currently active call
   *
   * Returns a copy of the active call object, or null if no call is active.
   * The call object includes: coachEasyrtcid, callType, conversationId,
   * coachName, status, and startTime.
   *
   * @returns {Object|null} A copy of the active call object, or null if no active call
   */
  getActiveCall() {
    return this.activeCall ? { ...this.activeCall } : null
  }
}

export default CallService
